import { LocalStorageKeys } from "@/enums";
import { IPanelScope } from "@/interfaces";

interface ICacheItem {
  expiredAt: number;
  value: any;
}

interface ICacheItems {
  [key: string]: ICacheItem | undefined;
}

const cacheService = () => {
  const cacheItems: ICacheItems = {};

  const getItemFromPersistCache = (
    scope: IPanelScope,
    key: LocalStorageKeys,
    fetchFromMemoryCache: boolean = false,
  ): any => {
    const cache_key = getLocalStorageKey(scope, key);
    let fromMemoryCache;

    if (fetchFromMemoryCache) {
      fromMemoryCache = getItemFromInMemoryCache(cache_key);
    }

    const item = fromMemoryCache || localStorage.getItem(cache_key);

    if (!item) return;

    try {
      return JSON.parse(item);
    } catch (e) {
      return item;
    }
  };

  const setItemToPersistCache = (
    scope: IPanelScope,
    key: LocalStorageKeys,
    value: any,
  ) => {
    const cache_key = getLocalStorageKey(scope, key);
    let parseValue = value;
    try {
      parseValue = JSON.stringify(value);
    } catch {
      /* empty */
    }

    value
      ? localStorage.setItem(cache_key, parseValue)
      : removeItemFromPersistCache(scope, key);
  };

  const removeItemFromPersistCache = (
    scope: IPanelScope,
    key: LocalStorageKeys,
  ) => {
    const cache_key = getLocalStorageKey(scope, key);
    localStorage.removeItem(cache_key);
  };

  const getItemFromInMemoryCache = (key: string): any => {
    const cacheItem = cacheItems[key];
    const nowTimeStamp = new Date().getTime();
    if (cacheItem?.expiredAt && cacheItem?.expiredAt < nowTimeStamp) {
      delete cacheItems[key];
    }

    return cacheItem?.value;
  };

  const saveItemToInMemoryCache = (
    key: string,
    value: any,
    cachePeriodInSeconds: number,
  ) => {
    cacheItems[key] = {
      expiredAt: new Date().getTime() + cachePeriodInSeconds,
      value: value,
    };
  };

  const clearInMemoryCacheByPrefix = () => {
    for (const key in cacheItems) {
      if (Object.prototype.hasOwnProperty.call(cacheItems, key)) {
        delete cacheItems[key];
      }
    }
  };

  const getLocalStorageKey = (
    { module, projectId, tab, userId }: IPanelScope,
    key: LocalStorageKeys,
  ): string => {
    let cache_key = "";

    const key_prefix = `${tab}-${module}-${projectId}`;
    switch (key) {
      case LocalStorageKeys.CLIENT_VERSION:
        cache_key = `client-version`;
        break;
      case LocalStorageKeys.FOLDERS_ITEMS_KEY:
      case LocalStorageKeys.FILTERS_ITEMS_KEY:
        cache_key = `${key_prefix}-data`;
        break;
      case LocalStorageKeys.SELECTED_TAB_KEY:
        cache_key = `${module}-${projectId}-tab`;
        break;
      case LocalStorageKeys.FAVORITE_KEY:
        cache_key = `${key_prefix}-${userId}-favorites`;
        break;
      case LocalStorageKeys.FAVORITE_TREE_COLLAPSE_STATE_KEY:
        cache_key = `${key_prefix}-${userId}-favorites-tree-collapse`;
        break;
      case LocalStorageKeys.FILTERS_TREE_COLLAPSE_STATE_KEY:
        cache_key = `${key_prefix}-${userId}-filters-tree-collapse`;
        break;
      default:
        console.error("Local storage key not found", key);
        return "";
    }

    return cache_key.toLowerCase();
  };

  const clearFilterCacheIfNeeded = (
    scope: IPanelScope,
    clientVersion: string,
  ) => {
    const cacheVersion = getItemFromPersistCache(
      scope,
      LocalStorageKeys.CLIENT_VERSION,
    );
    if (cacheVersion === clientVersion) {
      return;
    }

    const listOfFilterFolderKeys = ["filters-", "folders-"];
    Object.keys(localStorage).forEach((key) => {
      if (listOfFilterFolderKeys.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });

    setItemToPersistCache(
      scope,
      LocalStorageKeys.CLIENT_VERSION,
      clientVersion,
    );
  };

  return {
    getItemFromPersistCache,
    setItemToPersistCache,
    removeItemFromPersistCache,
    getLocalStorageKey,
    getItemFromInMemoryCache,
    saveItemToInMemoryCache,
    clearInMemoryCacheByPrefix,
    clearFilterCacheIfNeeded,
  };
};

export const CacheService = cacheService();
